/**
 * TaskDock — ปุ่มลอย "งานของฉัน" ข้างปุ่มแชท อยู่ทุกหน้า (29 ก.ย. 69)
 *
 * ผู้ใช้: "ไม่งั้นมันไม่เข้าไปดูกันว่าเหลืออะไร" — อยากได้ป๊อปอัปแบบแชท Messenger แยกสี แยกไอคอน
 *   ปุ่มส้มอำพัน ไอคอนคลิปบอร์ด · ตัวเลขแดง = งานที่ต้องลงมือตอนนี้ (เท่าตัวเลขแท็บ "งานของฉัน")
 *   มีงานเลยกำหนด = ขอบแดง + กะพริบ
 *   กด = หน้าต่างเล็ก เรียงตามความเร่ง: เลยกำหนด · ถูกตีกลับ · ติดปัญหา · รอคุณตรวจ · วันนี้–พรุ่งนี้ · อื่น ๆ
 *   ไม่เปิดเอง (27 ต.ค. 69 ผู้ใช้สั่งปิดการเด้งเอง — เดิมเปิดเองวันละครั้ง) · กดไอคอนบนหัวเว็บเพื่อดู
 *   ไม่แสดงในหน้า /tasks (อยู่ในหน้างานแล้ว)
 * ข้อมูลชุดเดียวกับหน้า /tasks (GET /api/tasks?box=mine&state=open) อัปเดตตามสัญญาณ task:event
 */
import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { ClipboardList, X, ChevronRight, ShieldCheck, Undo2, PauseCircle, AlertTriangle, CalendarClock, CircleDot } from "lucide-react";
import { useGetTasksQuery } from "../../slices/taskApiSlice";
import { StatusPill, DueCountdown, daysLeft, useTaskRefresh } from "./taskShared";
import { HeaderSlotPortal, HEADER_PANEL_POS, headerIconBtn } from "../ui/HeaderSlot";
import i18n from "../../i18n";
import { useLang } from "../../utils/useTx";

// 2 ภาษา TH/EN (23 ต.ค. 69) — T(ไทย, อังกฤษ) ตามภาษาปัจจุบัน · คอมโพเนนต์หลักเรียก useLang() ให้วาดใหม่ตอนสลับภาษา
const T = (th, en) => (i18n.language?.startsWith("en") ? en : th);

const todayKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
};
const SEEN_KEY = (uid) => `taskDockSeen:${uid}`;
const writeSeen = (uid) => { try { localStorage.setItem(SEEN_KEY(uid), todayKey()); } catch { /* storage ถูกบล็อก — เด้งซ้ำได้ ไม่เป็นไร */ } };

const GROUPS = [
  { key: "late", label: "เลยกำหนด", en: "Overdue", icon: AlertTriangle, tone: "text-rose-700" },
  { key: "rejected", label: "ถูกตีกลับ / ไม่ผ่านการตรวจ", en: "Sent back / failed review", icon: Undo2, tone: "text-rose-700" },
  { key: "blocked", label: "ติดปัญหา", en: "Blocked", icon: PauseCircle, tone: "text-amber-700" },
  { key: "review", label: "รอคุณตรวจ", en: "Waiting for your review", icon: ShieldCheck, tone: "text-violet-700" },
  { key: "soon", label: "วันนี้ – พรุ่งนี้", en: "Today – tomorrow", icon: CalendarClock, tone: "text-orange-700" },
  { key: "rest", label: "งานอื่นที่ค้าง", en: "Other pending tasks", icon: CircleDot, tone: "text-slate-600" },
];

const groupOf = (t) => {
  const n = daysLeft(t.due_date);
  if (n !== null && n < 0) return "late";
  if (t.rejected_reason) return "rejected";
  if (t.status === "BLOCKED") return "blocked";
  if (n !== null && n <= 1) return "soon";
  return "rest";
};

export default function TaskDock() {
  useLang();
  const { userInfo } = useSelector((s) => s.auth);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const hidden = !userInfo || pathname.startsWith("/tasks") || pathname.startsWith("/login");
  const { data, refetch } = useGetTasksQuery({ box: "mine", state: "open" }, { skip: !userInfo, pollingInterval: 300000 });
  useTaskRefresh(refetch);
  const [open, setOpen] = useState(false);

  const groups = useMemo(() => {
    const out = Object.fromEntries(GROUPS.map((g) => [g.key, []]));
    for (const t of data?.tasks || []) {
      if (t.status === "REVIEW" || t.status === "WAITING") continue; // ส่งตรวจแล้ว / ยังไม่ถึงคิว ไม่ต้องลงมือ
      out[groupOf(t)].push(t);
    }
    out.review = data?.toReview || [];
    return out;
  }, [data]);
  const count = Number(data?.counts?.mine) || 0;
  const late = groups.late.length;

  if (hidden) return null;
  const go = (path) => { setOpen(false); navigate(path); };

  return (
    <>
      {/* ปุ่มลอย — ซ้ายของปุ่มแชท */}
      {/* ปุ่มบนแถบหัวเว็บ (เดิมเป็นปุ่มลอยมุมขวาล่าง · ย้าย 20 ต.ค. 69) */}
      <HeaderSlotPortal>
        <button
          type="button"
          onClick={() => { setOpen((v) => !v); if (!open && userInfo?._id) writeSeen(userInfo._id); }}
          aria-pressed={open}
          title={count ? T(`งานที่ต้องทำ ${count} งาน${late ? ` · เลยกำหนด ${late}` : ""}`, `${count} tasks to do${late ? ` · ${late} overdue` : ""}`) : T("งานของฉัน — ไม่มีงานค้าง", "My tasks — nothing pending")}
          aria-label={T("งานของฉัน", "My tasks")}
          className={`${headerIconBtn} order-2 ${open ? "bg-amber-500 text-white border-amber-500" : "bg-amber-50 hover:bg-amber-100 text-amber-700 border-amber-200"} ${late ? "ring-2 ring-rose-500/70" : ""}`}
        >
          {late > 0 && <span className="absolute inset-0 rounded-full bg-rose-500 opacity-30 animate-ping motion-reduce:animate-none" />}
          <ClipboardList className="relative w-4 h-4" strokeWidth={2.2} />
          {count > 0 && (
            <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-[16px] px-1 rounded-full bg-rose-600 text-white text-[10px] font-black flex items-center justify-center ring-1 ring-white">
              {count > 99 ? "99+" : count}
            </span>
          )}
        </button>
      </HeaderSlotPortal>

      {/* หน้าต่างงาน */}
      {open && (
        <div
          role="dialog"
          aria-label={T("งานของฉัน", "My tasks")}
          className={`${HEADER_PANEL_POS} w-[min(24rem,calc(100vw-1.5rem))] max-h-[min(34rem,calc(100dvh-6rem))] flex flex-col rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200 overflow-hidden`}
        >
          <div className="shrink-0 px-4 py-3 bg-amber-500 text-white flex items-center gap-2">
            <ClipboardList className="w-5 h-5" />
            <div className="mr-auto">
              <div className="font-black leading-tight">{T("งานของฉัน", "My tasks")} · {count}</div>
              <div className="text-[12px] text-amber-50">{count ? T("เรียงตามเรื่องที่ต้องรีบก่อน", "Most urgent first") : T("ไม่มีงานที่ต้องทำตอนนี้", "Nothing to do right now")}</div>
            </div>
            <button type="button" onClick={() => setOpen(false)} className="p-1.5 rounded-lg hover:bg-white/20" aria-label={T("ปิด", "Close")}><X className="w-4 h-4" /></button>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto">
            {!count && (
              <p className="px-4 py-8 text-center text-sm text-slate-500">{T("เยี่ยมมาก ไม่มีงานค้าง 🎉", "Great — nothing pending 🎉")}</p>
            )}
            {GROUPS.map((g) => {
              const list = groups[g.key];
              if (!list.length) return null;
              const Icon = g.icon;
              return (
                <section key={g.key} className="py-1">
                  <div className={`px-4 pt-2 pb-1 text-[12px] font-black flex items-center gap-1.5 ${g.tone}`}>
                    <Icon className="w-3.5 h-3.5" /> {T(g.label, g.en)} · {list.length}
                  </div>
                  {list.slice(0, 6).map((t) => (
                    <button key={t.id} type="button" onClick={() => go(`/tasks/${t.id}`)} className="w-full text-left px-4 py-2 flex items-center gap-2 hover:bg-slate-50">
                      <span className="min-w-0 flex-1">
                        <span className="block text-[14px] font-semibold text-slate-900 truncate">{t.title}</span>
                        <span className="flex items-center gap-1.5 mt-0.5">
                          <span className="font-mono text-[11px] text-slate-400">{t.task_no}</span>
                          <StatusPill status={t.status} />
                          <DueCountdown due={t.due_date} status={t.status} />
                        </span>
                      </span>
                      <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
                    </button>
                  ))}
                  {list.length > 6 && <div className="px-4 pb-1 text-[12px] text-slate-400">{T(`และอีก ${list.length - 6} งาน`, `and ${list.length - 6} more`)}</div>}
                </section>
              );
            })}
          </div>
          <div className="shrink-0 border-t border-slate-100 p-2">
            <button type="button" onClick={() => go("/tasks")} className="w-full rounded-xl bg-slate-900 py-2.5 text-sm font-bold text-white hover:bg-slate-700">
              {T("ดูงานทั้งหมด →", "See all tasks →")}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
